import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { unwrapConnection } from "@utils/utils";
import { FC, PropsWithChildren, createContext, useContext, useEffect, useRef, useState } from "react";

import {
  GetNotificationsQuery,
  NotificationsType,
  useDismissNotificationMutation,
  useGetNotificationsQuery,
  useReadAllNotificationsMutation,
  useReadNotificationMutation,
} from "@api/graphql/types-and-hooks";
import { notifyError } from "@components/notifications/notifications";
import { useAdminData } from "@providers/AdminDataProvider";
import { getNotificationNavigationUrl } from "@providers/notificationNavigation";

interface NotificationsContextType {
  notifications: NotificationsType[];
  setNotifications: React.Dispatch<React.SetStateAction<NotificationsType[]>>;
  totalUnread: number;
  setTotalUnread: React.Dispatch<React.SetStateAction<number>>;
  refetchNotifications: (options?: RefetchOptions) => Promise<QueryObserverResult<GetNotificationsQuery, unknown>>;
  readNotification: (notification: NotificationsType) => Promise<string | undefined>;
  readAllNotifications: () => Promise<void>;
  dismissNotification: (notification: NotificationsType) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

export const NotificationsProvider: FC<PropsWithChildren> = ({ children }) => {
  const [notifications, setNotifications] = useState<NotificationsType[]>([]);
  const [totalUnread, setTotalUnread] = useState<number>(0);
  const { isUserCaregiver, roles } = useAdminData();

  const readNotificationMutation = useReadNotificationMutation({});
  const readAllNotificationsMutation = useReadAllNotificationsMutation({});
  const dismissNotificationMutation = useDismissNotificationMutation({});
  const pendingDismissIds = useRef<Set<string>>(new Set());

  const {
    data: notificationsData,
    error: notificationsError,
    refetch: refetchNotifications,
  } = useGetNotificationsQuery(
    {
      first: 50,
    },
    {
      queryKey: ["getNotifications"],
      enabled: !!roles.length && !isUserCaregiver,
    },
  );

  // log notifications error but don't return early - always render the provider
  // toast is handled upstream in handleApiError (only for actual FDM response errors)
  useEffect(() => {
    if (notificationsError) {
      console.error("Failed to load notifications:", notificationsError);
    }
  }, [notificationsError]);

  useEffect(() => {
    const allNotifications = (unwrapConnection(notificationsData?.getNotifications) || []) as NotificationsType[];
    const serverTotalUnread = notificationsData?.getNotifications?.totalUnread || 0;

    // Filter out notifications that are currently being dismissed to prevent
    // refetch results from restoring optimistically removed notifications
    const filtered = allNotifications.filter((n) => !pendingDismissIds.current.has(n.id));
    const pendingUnreadCount = allNotifications.filter((n) => pendingDismissIds.current.has(n.id) && !n.read).length;
    setNotifications(filtered);
    setTotalUnread(Math.max(0, serverTotalUnread - pendingUnreadCount));
  }, [notificationsData]);

  /**
   * marks the specified notification as read, refetches the notitfication list, and returns the redirect path if applicable
   * @param notification the notification to mark read
   * @returns the redirect path
   */
  const readNotification = async (notification: NotificationsType): Promise<string | undefined> => {
    // Optimistically update UI immediately
    if (!notification.read) {
      setNotifications((prev) => prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n)));
      setTotalUnread((prev) => Math.max(0, prev - 1));
    }

    try {
      const response = await readNotificationMutation.mutateAsync({
        input: {
          notificationId: notification.id,
        },
      });

      if (!response.readNotification?.status) {
        notifyError(response.readNotification?.message || "Failed to mark notification as read");
        refetchNotifications();
        return;
      }

      refetchNotifications();

      return getNotificationNavigationUrl(notification);
    } catch (error) {
      console.error("Error marking notification as read:", error);
      notifyError("Failed to mark notification as read");
      // Revert optimistic update on error
      refetchNotifications();
      return;
    }
  };

  /**
   * marks all notifications as read and refetches the notification list
   */
  const readAllNotifications = async (): Promise<void> => {
    // Optimistically update UI immediately
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setTotalUnread(0);

    try {
      const response = await readAllNotificationsMutation.mutateAsync({});

      if (!response.readAllNotifications?.status) {
        notifyError(response.readAllNotifications?.message || "Failed to mark all notifications as read");
      }

      refetchNotifications();
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      notifyError("Failed to mark all notifications as read");
      // Revert optimistic update on error
      refetchNotifications();
    }
  };

  const dismissNotification = async (notification: NotificationsType): Promise<void> => {
    // Guard: already being dismissed
    if (pendingDismissIds.current.has(notification.id)) return;
    pendingDismissIds.current.add(notification.id);

    // Optimistically remove from list and decrement unread if needed
    setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
    if (!notification.read) {
      setTotalUnread((prev) => Math.max(0, prev - 1));
    }

    try {
      const response = await dismissNotificationMutation.mutateAsync({
        input: {
          notificationId: notification.id,
        },
      });

      if (!response.dismissNotification?.status) {
        notifyError(response.dismissNotification?.message || "Failed to dismiss notification");
        // Clear pending ID before refetch so the useEffect doesn't filter it out
        pendingDismissIds.current.delete(notification.id);
        refetchNotifications();
        return;
      }
    } catch (error) {
      console.error("Error dismissing notification:", error);
      notifyError("Failed to dismiss notification");
      // Clear pending ID before refetch so the useEffect doesn't filter it out
      pendingDismissIds.current.delete(notification.id);
      refetchNotifications();
      return;
    }
    pendingDismissIds.current.delete(notification.id);
  };

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        setNotifications,
        totalUnread,
        setTotalUnread,
        refetchNotifications,
        readNotification,
        readAllNotifications,
        dismissNotification,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotificationsData = () => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error("useNotificationsData must be used within a NotificationsProvider");
  }
  return context;
};
